// PhotoPage.vue
<template>
  <div class="photo-page container mt-5">
    <h1>Photos</h1>

    <!-- Barre de recherche -->
    <b-form-group label="Recherche">
      <b-form-input
          v-model="searchQuery"
          placeholder="Nom de la photo..."
          @input="onSearchInput"
      />
      <small class="form-text text-muted">Tapez au moins 3 caractères pour lancer la recherche.</small>
    </b-form-group>

    <!-- Résultats -->
    <div v-if="photos.length">
      <ul class="list-group">
        <li
            v-for="photo in photos"
            :key="photo.id"
            class="list-group-item d-flex justify-content-between align-items-center"
        >
          <span>{{ photo.nom }}</span>
          <div>

            <b-button size="sm" variant="info" @click="openViewModal(photo)">
              Voir
            </b-button>

            <template v-if="photo.interventions && photo.interventions.length">
              <b-button
                  v-for="interv in photo.interventions"
                  :key="`${interv.type}-${interv.id}`"
                  size="sm"
                  :variant="interv.type === 'client' ? 'info' : 'success'"
                  class="ms-2"
                  @click="openInterventionModalById(interv.id)"
              >

                Intervention #{{ interv.id }}
              </b-button>

            </template>
              <b-button size="sm" variant="danger" class="ms-2" @click="deletePhoto(photo)">
                X
              </b-button>
          </div>
        </li>
      </ul>
    </div>
    <div v-else-if="searchQuery.length >= minSearchLength" class="alert alert-info">
      Aucun résultat.
    </div>

    <!-- Modal d'affichage de la photo -->
    <b-modal v-model="showViewModal" :title="viewPhoto?.nom || 'Photo'" hide-footer size="lg">
      <div class="photo-container" :class="{ 'zoomed': isZoomed }">
        <img 
          :src="viewImageUrl" 
          alt="Photo" 
          class="photo-image"
          :class="{ 'zoomed': isZoomed, 'dragging': isDragging }"
          :style="{ transform: isZoomed ? `scale(2) translate(${imagePosition.x}px, ${imagePosition.y}px)` : 'scale(1)' }"
          @dblclick="toggleZoom"
          @mousedown="startDrag"
          @mousemove="drag"
          @mouseup="endDrag"
          @mouseleave="endDrag"
        />
      </div>
      <div class="text-center mt-2">
        <small class="text-muted">Double-cliquez pour zoomer/dézoomer • Glissez-déposez quand zoomé</small>
      </div>
    </b-modal>

    <!-- Modal création d'une nouvelle photo -->
    <b-modal v-model="showNewModal" title="Nouvelle Photo" hide-footer>
      <b-form @submit.prevent="submitNewPhoto">
        <b-form-group label="Nom de la photo">
          <b-form-input v-model="newPhoto.nom" required />
        </b-form-group>

        <b-form-group label="Fichier image / Photo">
          <!-- Mode fichier classique -->
          <div v-if="!usingCamera">
            <input
                type="file"
                class="form-control mb-2"
                accept="image/*"
                @change="onNativeFileChange"
            />
            <b-button
                variant="outline-primary"
                class="w-100 mb-2"
                @click="startCamera"
            >
              Prendre une photo
            </b-button>
          </div>

          <!-- Mode caméra live -->
          <div v-else class="text-center">
            <video ref="video" autoplay playsinline style="width:100%; max-height:300px;"></video>
            <div class="mt-2">
              <b-button variant="success" @click="captureFromCamera">Capturer</b-button>
              <b-button variant="danger" class="ms-2" @click="stopCamera">Annuler</b-button>
            </div>
          </div>

          <!-- Aperçu de l'image capturée ou choisie -->
          <div v-if="previewImage" class="mt-3 text-center">
            <img :src="previewImage" alt="Aperçu" style="max-width:100%; height:auto;" />
          </div>

          <!-- Canvas caché pour capture -->
          <canvas ref="canvas" style="display:none"></canvas>
        </b-form-group>

        <div class="text-end">
          <b-button type="submit" variant="success">
            Enregistrer
          </b-button>
        </div>
      </b-form>
    </b-modal>
    <b-modal
        v-model="showInterventionModal"
        id="interventionModal"
        title="Détails de l'intervention"
        hide-footer
    >
      <div v-if="interventionToShow">
        <p><strong>ID :</strong> #{{ interventionToShow.id }}</p>
        <p><strong>Titre :</strong> {{ interventionToShow.title }}</p>
        <p><strong>Client :</strong> {{ interventionToShow.client_name || '— Aucun client —' }}</p>
        <p><strong>Utilisateur :</strong> {{ interventionToShow.user_name }}</p>
        <p><strong>Date intervention :</strong> {{ formatDateFR(interventionToShow.date_intervention) }}</p>
        <p v-if="interventionToShow.heure_arrivee && interventionToShow.heure_depart">
          <strong>Heure d'arrivée :</strong> {{ interventionToShow.heure_arrivee }}
        </p>
        <p v-if="interventionToShow.heure_arrivee && interventionToShow.heure_depart">
          <strong>Heure de départ :</strong> {{ interventionToShow.heure_depart }}
        </p>
        <p v-if="!interventionToShow.heure_arrivee || !interventionToShow.heure_depart" class="text-muted">
          <i class="fas fa-info-circle me-2"></i>
          Cette intervention n'a pas de durée définie (pas d'heure d'arrivée ni de départ).
        </p>
        <p v-if="interventionToShow.duree_intervention">
          <strong>Durée :</strong> {{ interventionToShow.duree_intervention }}
        </p>
        <div v-if="interventionToShow.memo_client">
          <p><strong>Mémo client :</strong></p>
          <div class="memo-display" v-html="cleanMemoHtml(interventionToShow.memo_client)"></div>
        </div>
        <div v-else>
          <p><strong>Mémo client :</strong> —</p>
        </div>
        
        <div v-if="interventionToShow.memo_utilisateur">
          <p><strong>Mémo utilisateur :</strong></p>
          <div class="memo-display" v-html="cleanMemoHtml(interventionToShow.memo_utilisateur)"></div>
        </div>
        <div v-else>
          <p><strong>Mémo utilisateur :</strong> —</p>
        </div>
        <p><strong>Responsable :</strong> {{ interventionToShow.accountable || '—' }}</p>
        <p>
          <strong>Signature :</strong>
          <span v-if="interventionToShow.signature">
      <img
          :src="`data:image/png;base64,${interventionToShow.signature}`"
          alt="Signature"
          style="max-width: 200px; border:1px solid #ccc;"
      />
    </span>
          <span v-else>Non signée</span>
        </p>
      </div>
      <div v-else>
        Chargement…
      </div>
      <b-button variant="secondary" @click="showInterventionModal = false">
        Fermer
      </b-button>
    </b-modal>

    <!-- Footer avec FooterActions -->
    <FooterActions :buttons="footerButtons" />
  </div>
</template>

<script>
import FooterActions from '@/components/FooterActions.vue';
import api from '@/plugins/api.js';

export default {
  name: 'PhotoPage',
  components: { FooterActions },
  data() {
    return {
      searchQuery: '',
      photos: [],
      showViewModal: false,
      viewImageUrl: '',
      showNewModal: false,
      newPhoto: { nom: '', file: null },
      previewImage: null,
      usingCamera: false,
      videoStream: null,
      minSearchLength: 3,
      showInterventionModal: false,
      interventionToShow: null,
      viewPhoto: null,
      isZoomed: false,
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      imagePosition: { x: 0, y: 0 },
    };
  },
  computed: {
    footerButtons() {
      return [
        { text: 'NEW', onClick: this.openNewModal },
        { text: 'Accueil', onClick: this.goBack, variant: 'secondary' }
      ];
    }
  },
  methods: {
    // Fonction pour nettoyer le HTML du mémo
    cleanMemoHtml(html) {
      if (!html) return '';
      return html
        .replace(/<p>/g, '')
        .replace(/<\/p>/g, '<br>')
        .replace(/<br><br>/g, '<br>') // Éviter les doubles sauts de ligne
        .replace(/<br>$/g, ''); // Supprimer le dernier <br>
    },

    onSearchInput() {
      if ((this.searchQuery.length >= this.minSearchLength) || this.searchQuery.length === 0) {
        this.searchPhotos();
      }else {
        this.photos = [];
      }
    },
    searchPhotos() {
      const url = ((this.searchQuery.length >= this.minSearchLength) || this.searchQuery.length === 0)
          ? `/photos/?search=${encodeURIComponent(this.searchQuery)}&include_images=false`
          : `/photos/?include_images=false`;

      api.get(url)
          .then(res => this.photos = res.data)
          .catch(err => { console.error(err); this.photos = []; });
    },

    formatDateFR(isoDate) {
      if (!isoDate) return '';
      const d = new Date(isoDate);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },

    async openViewModal(photo) {
      // Récupère les détails (image complète/base64) seulement quand on clique sur "Voir"
      const { data } = await api.get(`/photos/${photo.id}/`);
      this.viewPhoto = {
        ...photo,       // garde les champs déjà présents (nom, id…)
        image: data.image // met à jour l'image détaillée/base64 reçue
      };
      this.viewImageUrl = `data:image/jpeg;base64,${data.image}`;
      this.isZoomed = false; // Réinitialiser le zoom
      this.imagePosition = { x: 0, y: 0 }; // Réinitialiser la position
      this.showViewModal = true;
    },

    toggleZoom() {
      this.isZoomed = !this.isZoomed;
      if (!this.isZoomed) {
        // Réinitialiser la position quand on dézoome
        this.imagePosition = { x: 0, y: 0 };
      } else {
        // Centrer la photo quand on zoome
        this.imagePosition = { x: 0, y: 0 };
      }
    },

    startDrag(event) {
      if (!this.isZoomed) return;
      this.isDragging = true;
      this.dragStart = {
        x: event.clientX - this.imagePosition.x,
        y: event.clientY - this.imagePosition.y
      };
      event.preventDefault();
    },

    drag(event) {
      if (!this.isDragging || !this.isZoomed) return;
      
      let newX = event.clientX - this.dragStart.x;
      let newY = event.clientY - this.dragStart.y;
      
      const container = event.target.parentElement;
      const containerRect = container.getBoundingClientRect();
      const imageRect = event.target.getBoundingClientRect();
      
      // Calculer les limites maximales de translation (unscaled)
      // L'image est zoomée 2x, donc les valeurs de translation doivent être divisées par 2
      const maxTranslateX = Math.max(0, (imageRect.width - containerRect.width) / 4);
      const maxTranslateY = Math.max(0, (imageRect.height - containerRect.height) / 4);
      
      // Contrainte les valeurs de déplacement
      newX = Math.max(-maxTranslateX, Math.min(maxTranslateX, newX));
      newY = Math.max(-maxTranslateY, Math.min(maxTranslateY, newY));

      this.imagePosition = { x: newX, y: newY };
      event.preventDefault();
    },

    endDrag() {
      this.isDragging = false;
    },
    openNewModal() {
      this.newPhoto = { nom: '', file: null };
      this.showNewModal = true;
    },


    async deletePhoto(photo) {
      if (!confirm(`Supprimer la photo "${photo.nom}" ?`)) return;
      try {
        await api.delete(`/photos/${photo.id}`);
        // On enlève la photo du tableau sans recharger toute la liste (optimisé) :
        this.photos = this.photos.filter(p => p.id !== photo.id);
      } catch (err) {
        alert("Erreur lors de la suppression.");
        console.error(err);
      }
    },


    async openInterventionModalById(id) {
      try {
        const { data } = await api.get(`/interventions/${id}/`);
        this.interventionToShow = data;
        this.showInterventionModal = true;
      } catch (e) {
        console.error("Erreur chargement intervention :", e);
      }
    },

    goBack() {
      this.$router.push('/Accueil');
    },

    onNativeFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.newPhoto.file = file;
      const reader = new FileReader();
      reader.onload = e => this.previewImage = e.target.result;
      reader.readAsDataURL(file);
    },
    async startCamera() {
      try {
        this.usingCamera = true;
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            facingMode: { ideal: 'environment' }
          }
        });
        this.videoStream = stream;
        this.$refs.video.srcObject = stream;
      } catch (err) {
        console.error('Impossible d’accéder à la caméra :', err);
        this.usingCamera = false;
      }
    },
    stopCamera() {
      if (this.videoStream) {
        this.videoStream.getTracks().forEach(t => t.stop());
        this.videoStream = null;
      }
      this.usingCamera = false;
    },
    async captureFromCamera() {
      const video  = this.$refs.video;
      const canvas = this.$refs.canvas;

      // Dimensions natives
      const nativeWidth  = video.videoWidth;
      const nativeHeight = video.videoHeight;

      // Objectif : fixer une hauteur cible (1080 px), recalculer la largeur
      const targetHeight = 1080;
      const targetWidth = Math.round((nativeWidth / nativeHeight) * targetHeight);

      canvas.width  = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, targetWidth, targetHeight);

      // Encodage en JPEG compressé
      canvas.toBlob(blob => {
        const reader = new FileReader();
        reader.onload = e => {
          const dataUrl = e.target.result;
          this.previewImage = dataUrl;
          this.newPhoto.file = dataUrl.split(',')[1];
        };
        reader.readAsDataURL(blob);
      }, 'image/jpeg', 0.85); // qualité à 85% (très bon compromis)

      this.stopCamera();
    },
    submitNewPhoto() {
      if (!this.previewImage) return;
      api.post('/photos/', {
        nom: this.newPhoto.nom,
        image: this.newPhoto.file
      })
          .then(() => {
            this.showNewModal = false;
            this.searchPhotos();
          })
          .catch(err => console.error(err));
    },
  },
  mounted() {
    this.searchPhotos();
  }
};
</script>

<style scoped>
.photo-page {
  padding-bottom: 120px; /* espace pour le footer */
}

/* Styles pour l'affichage des mémo */
.memo-display {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.75rem;
  margin: 0.5rem 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.5;
  color: #495057;
  font-family: inherit;
  font-size: 0.9rem;
}

/* Styles pour le système de zoom */
.photo-container {
  text-align: center;
  overflow: hidden;
  max-height: 70vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.photo-container.zoomed {
  overflow: hidden; /* Garder hidden pour les limites */
  max-height: 80vh;
}

.photo-image {
  max-width: 100%;
  max-height: 100%;
  cursor: pointer;
  transition: transform 0.3s ease;
  object-fit: contain;
  user-select: none;
}

.photo-image.zoomed {
  cursor: grab;
  transition: none; /* Pas de transition pendant le drag */
}

.photo-image.zoomed.dragging {
  cursor: grabbing;
}

.photo-image.zoomed:active {
  cursor: grabbing;
}
</style>
